import {
  db,
  docsFromSnapshot,
  docFromSnapshot,
  functions
} from "../../firebase";
import _ from "lodash";
import {
  INVOICE_LIST_FIRST_PAGE,
  INVOICE_LIST_NEXT_PAGE,
  buildPaidInvoiceUpdate
} from "@/helpers/invoices";

export const DEFAULT_INVOICE = {
  number: "INV",
  paymentReference: "SB-1",
  sentDate: null,
  dueDate: null,
  status: "ISSUED",
  from: {
    name: null,
    emailAddress: null,
    vatNumber: null,
    address: {
      line1: null,
      line2: null,
      district: null,
      region: null,
      country: null,
      postcode: null
    }
  },
  to: {
    companyName: null,
    emailAddress: null,
    vatNumber: null,
    address: {
      line1: null,
      line2: null,
      district: null,
      region: null,
      country: null,
      postcode: null
    }
  },
  items: [
    {
      description: null,
      price: null,
      quantity: "1",
      vat: null
    }
  ],
  subTotal: {
    amount: 0,
    displayValue: "€0.00"
  },
  vatTotal: {
    amount: 0,
    displayValue: "€0.00"
  },
  total: {
    amount: 0,
    displayValue: "€0.00"
  },
  terms: null,
  notes: null,
  // recurring config
  paymentDays: 30,
  issuedCount: 0,
  creditNotes: [],
  refunds: [],
  deleted: false
};

const state = {
  invoices: {},
  invoice: null,
  invoiceNumberHistory: {
    nextNumber: "INV0001",
    usedNumbers: []
  },
  invoiceNumberPoller: null
};

const getters = {
  invoices: state => _.values(state.invoices),
  invoicesCount: state => _.values(state.invoices).length | 0,
  invoice: state => state.invoice,
  nextInvoiceNumber: state => state.invoiceNumberHistory.nextNumber,
  invoiceNumberPoller: state => state.invoiceNumberPoller
};

const saveInvoice = functions.httpsCallable("saveInvoice");
const listInvoiceSummaries = functions.httpsCallable("listInvoiceSummaries");

async function fetchInvoiceDocsLegacy(tenantId) {
  const snapshot = await db
    .collection("invoices")
    .where("tenantId", "==", tenantId)
    .get();

  // Client filter so docs missing `deleted` still show (legacy invoices)
  const docs = docsFromSnapshot(snapshot);
  return _.pickBy(docs, inv => inv && inv.deleted !== true);
}

const actions = {
  async fetchInvoices({ commit, dispatch, getters }) {
    const tenantId = getters.tenantId;
    try {
      const result = await listInvoiceSummaries({
        limit: INVOICE_LIST_FIRST_PAGE
      });
      const invoices = (result.data && result.data.invoices) || {};
      commit("setInvoices", invoices);
      if (result.data && result.data.hasMore) {
        dispatch("fetchRemainingInvoiceSummaries", result.data);
      }
      return;
    } catch (error) {
      // Function not deployed yet, or timeout — keep the list working.
    }
    commit("setInvoices", await fetchInvoiceDocsLegacy(tenantId));
  },
  async fetchRemainingInvoiceSummaries({ commit, dispatch }, page) {
    if (!page || !page.hasMore || !page.nextId) {
      return;
    }
    try {
      const result = await listInvoiceSummaries({
        limit: INVOICE_LIST_NEXT_PAGE,
        startAfterId: page.nextId,
        startAfterNumber: page.nextNumber
      });
      commit("mergeInvoices", (result.data && result.data.invoices) || {});
      if (result.data && result.data.hasMore) {
        await dispatch("fetchRemainingInvoiceSummaries", result.data);
      }
    } catch (error) {
      // First page is already on screen; search may be incomplete.
    }
  },
  async fetchInvoice({ commit }, invoiceId) {
    commit("resetInvoice");

    const snapshot = await db
      .collection("invoices")
      .doc(invoiceId)
      .get();

    const invoice = docFromSnapshot(snapshot);
    if (invoice) {
      commit("setInvoice", invoice);
    }
  },
  addInvoice(context, invoice) {
    invoice = {
      ...invoice,
      tenantId: context.getters.tenantId,
      timestamp: +new Date()
    };

    return saveInvoice({ invoice });
  },
  async setInvoiceStatusToPaid(context, invoice) {
    const invoiceId = invoice && (invoice._id || invoice.id);
    if (!invoiceId) {
      throw new Error("Invoice id is missing; cannot mark as paid.");
    }
    const snapshot = await db
      .collection("invoices")
      .doc(invoiceId)
      .get();
    const current = docFromSnapshot(snapshot);
    const paid = buildPaidInvoiceUpdate(current, invoiceId, +new Date());

    await db
      .collection("invoices")
      .doc(invoiceId)
      .set(paid);

    context.commit("setInvoice", paid);
  },
  async fetchInvoiceNumberHistory({ commit, getters }) {
    const snapshot = await db
      .collection("invoice-number-history")
      .doc(getters.tenantId)
      .get();

    const doc = docFromSnapshot(snapshot);
    if (doc) {
      commit("setInvoiceNumberHistory", doc);
    }
  },
  startPollingInvoiceNumberHistory(context) {
    // Avoid stacking intervals if start is called twice
    context.dispatch("stopPollingInvoiceNumberHistory");
    context.dispatch("fetchInvoiceNumberHistory");

    const poller = setInterval(
      () => context.dispatch("fetchInvoiceNumberHistory"),
      1000
    );

    context.commit("setInvoiceNumberPoller", poller);
  },
  stopPollingInvoiceNumberHistory(context) {
    const poller = context.getters.invoiceNumberPoller;
    if (poller) {
      clearInterval(poller);
    }
    context.commit("setInvoiceNumberPoller", null);
  }
};

const mutations = {
  setInvoices(state, invoices) {
    state.invoices = invoices;
  },
  mergeInvoices(state, invoices) {
    state.invoices = { ...state.invoices, ...invoices };
  },
  setInvoice(state, invoice) {
    if (!invoice) {
      state.invoice = null;
      return;
    }
    const invoiceId = invoice._id || invoice.id;
    if (invoiceId) {
      state.invoices[invoiceId] = { ...invoice, _id: invoiceId, id: invoiceId };
      state.invoice = state.invoices[invoiceId];
    } else {
      state.invoice = invoice;
    }
  },
  resetInvoice(state) {
    state.invoice = null;
  },
  setInvoiceNumberHistory(state, invoiceNumberHistory) {
    state.invoiceNumberHistory = invoiceNumberHistory;
  },
  setInvoiceNumberPoller(state, poller) {
    state.invoiceNumberPoller = poller;
  }
};

export default {
  state,
  actions,
  getters,
  mutations
};
